import { useState, memo } from 'react';
import { cn } from '@/lib/utils/utils';
import { useUrl } from 'crossroad';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
    MessageSquare,
    Search,
    Lightbulb,
    Heart,
    Sparkles,
    Target,
    HelpCircle,
    Users,
    CheckCircle2
} from 'lucide-react';
import { useStudyGuideByPassageData } from '@/data/study-guide/study-guide-by-passage-data-context';
import { AIDisclaimerBadge } from '../ui/ai-disclaimer-badge';
import { DiscoveryCard } from './discovery-card';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { ContextHero } from '../context/ContextHero';

// Hero illustration
import studyGuideHero from '@/assets/heroes/study_guide_hero_v3.png';

interface Props {
    passageId: string;
}

const getDifficultyColors = (difficulty: string, isDarkMode: boolean): string => {
    const colors: Record<string, string> = {
        basic: isDarkMode ? 'bg-green-500/20 text-green-300 border-green-500/30' : 'bg-green-100 text-green-800 border-green-300',
        reflective: isDarkMode ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' : 'bg-yellow-100 text-yellow-800 border-yellow-300',
        complex: isDarkMode ? 'bg-red-500/20 text-red-300 border-red-500/30' : 'bg-red-100 text-red-800 border-red-300'
    };
    return colors[difficulty] || '';
};

const DOMAIN_ICONS = {
    internal: Heart,
    relational: Users,
    societal: MessageSquare,
    vocational: Target,
    spiritual: Sparkles
};

export default function PassageInductiveStudy({ passageId }: Props) {
    const isDarkMode = useDarkMode();
    const [, setUrl] = useUrl();
    const { data: studyGuide, isLoading } = useStudyGuideByPassageData(passageId);

    if (isLoading) {
        return <LoadingSkeleton />;
    }

    if (!studyGuide) {
        return (
            <div className="flex flex-col items-center justify-center p-8 text-center space-y-4">
                <div className="bg-muted p-4 rounded-full">
                    <HelpCircle className="h-8 w-8 text-muted-foreground" />
                </div>
                <div className="space-y-2">
                    <h3 className="text-lg font-semibold">Study Guide Not Available</h3>
                    <p className="text-sm text-muted-foreground max-w-md">
                        A study guide hasn't been generated for this passage yet. Check back later.
                    </p>
                </div>
            </div>
        );
    }

    const {
        exegeticalPoint,
        observationQuestions,
        interpretationQuestions,
        applicationQuestions
    } = studyGuide;

    return (
        <div className="relative space-y-6">
            <ContextHero
                eyebrow="INDUCTIVE STUDY"
                headline="Observe, Interpret, Synthesize, Apply"
                subtitle="A structured framework of investigative questions to help you uncover the text's central message."
                image={studyGuideHero}
                onClose={() => setUrl(`/app/passage/${passageId}`)}
                showClose={false}
                showHandle={false}
                className="mb-8"
            />

            {/* Observation Questions */}
            <div className="space-y-6 px-4">
                <div className="space-y-1">
                    <h2 className="text-2xl font-serif font-semibold tracking-tight text-foreground/90">
                        Observation Questions
                    </h2>
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/60">
                            What do you see in the text?
                        </span>
                        <div className="h-px w-8 bg-primary/20" />
                    </div>
                </div>
                <QuestionList questions={observationQuestions} type="study" isDarkMode={isDarkMode} />
            </div>

            {/* Interpretation Questions */}
            <div className="space-y-6 pt-12 px-4">
                <div className="space-y-1">
                    <h2 className="text-2xl font-serif font-semibold tracking-tight text-foreground/90">
                        Interpretation Questions
                    </h2>
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/60">
                            What does it mean?
                        </span>
                        <div className="h-px w-8 bg-primary/20" />
                    </div>
                </div>
                <QuestionList questions={interpretationQuestions} type="study" isDarkMode={isDarkMode} />
            </div>

            {/* Exegetical Big Idea */}
            <div className="space-y-12">
                <div className="px-4 pt-12">
                    <div className="space-y-1 mb-6">
                        <h2 className="text-2xl font-serif font-semibold tracking-tight text-foreground/90">
                            Study Goal: The Big Idea
                        </h2>
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/60">
                                Synthesize the passage into its central message and supporting logical flow.
                            </span>
                            <div className="h-px w-8 bg-primary/20" />
                        </div>
                    </div>

                    <DiscoveryCard
                        isExpanded={true}
                        onToggle={() => { }}
                        header={
                            <div className="space-y-3 w-full border-l-4 border-l-primary pl-4 py-1">
                                <div className="flex items-center gap-2 text-primary/60">
                                    <Sparkles className="h-3 w-3" />
                                    <span className="text-[9px] font-bold uppercase tracking-[0.2em]">Example Big Idea</span>
                                </div>
                                <div className="space-y-2">
                                    <h4 className="text-xl font-serif font-medium italic leading-relaxed text-foreground/90">
                                        "{exegeticalPoint.mainPoint}"
                                    </h4>
                                    {exegeticalPoint.argumentFlow && (
                                        <p className="text-sm text-muted-foreground leading-relaxed">
                                            <span className="font-semibold text-foreground">Flow of Argument:</span> {exegeticalPoint.argumentFlow}
                                        </p>
                                    )}
                                </div>
                            </div>
                        }
                    >
                        <div className="space-y-4">
                            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/50 flex items-center gap-2">
                                <Sparkles className="h-3 w-3" />
                                Supporting Points
                            </div>
                            <div className="space-y-3">
                                {exegeticalPoint.subPoints.map((sub, idx) => (
                                    <div key={idx} className="flex gap-3 items-start group">
                                        <div className="bg-primary/10 text-primary text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center mt-0.5 group-hover:bg-primary group-hover:text-primary-foreground transition-colors shrink-0">
                                            {idx + 1}
                                        </div>
                                        <div className="flex-1 space-y-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-semibold text-sm">{sub.point}</span>
                                                {sub.verseRef && (
                                                    <Badge variant="secondary" className="text-[10px] px-1.5 h-4">
                                                        {sub.verseRef}
                                                    </Badge>
                                                )}
                                            </div>
                                            {sub.explanation && (
                                                <p className="text-xs text-muted-foreground leading-relaxed">
                                                    {sub.explanation}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </DiscoveryCard>
                </div>
            </div>

            {/* Application Questions */}
            <div className="space-y-6 pt-12 px-4">
                <div className="space-y-1">
                    <h2 className="text-2xl font-serif font-semibold tracking-tight text-foreground/90">
                        Application Questions
                    </h2>
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/60">
                            How should this change your life?
                        </span>
                        <div className="h-px w-8 bg-primary/20" />
                    </div>
                </div>
                <QuestionList questions={applicationQuestions} type="application" isDarkMode={isDarkMode} />
            </div>

            {/* AI Disclaimer */}
            <div className="py-8 border-t border-border/50 flex flex-col items-center gap-4">
                <AIDisclaimerBadge
                    model={studyGuide?.generatedBy || (studyGuide as any)?.source}
                    passageId={passageId}
                    contextType="study-guide"
                />
                <p className="text-[10px] text-muted-foreground text-center max-w-[400px]">
                    This study guide synthesizes exegetical insights and application questions to help you engage deeply with Scripture.
                </p>
            </div>
        </div>
    );
}

function QuestionList({ questions, type, isDarkMode }: { questions: (StudyQuestion | ApplicationQuestion)[], type: 'study' | 'application', isDarkMode: boolean }) {
    if (questions.length === 0) {
        return <p className="text-sm text-muted-foreground italic text-center py-8">No questions available for this category.</p>;
    }

    return (
        <div className="space-y-4">
            {questions.map((q, idx) => (
                <QuestionItem key={idx} question={q} type={type} isDarkMode={isDarkMode} />
            ))}
        </div>
    );
}

const QuestionItem = memo(({ question, type, isDarkMode }: { question: any, type: 'study' | 'application', isDarkMode: boolean }) => {
    const isApplication = type === 'application';
    const [isExpanded, setIsExpanded] = useState(isApplication);
    const [isAnswerRevealed, setIsAnswerRevealed] = useState(isApplication); // Show answer immediately for application
    const studyQ = type === 'study' ? question as StudyQuestion : undefined;
    const appQ = type === 'application' ? question as ApplicationQuestion : undefined;

    const Icon = appQ?.domain ? (DOMAIN_ICONS[appQ.domain as keyof typeof DOMAIN_ICONS] || Target) : Search;

    return (
        <DiscoveryCard
            isExpanded={isExpanded}
            onToggle={() => setIsExpanded(!isExpanded)}
            canExpand={!isApplication}
            header={
                <div className="flex-1 min-w-0 pr-2">
                    <div className="flex justify-between items-start gap-3">
                        <p className="font-medium text-sm text-foreground/90 leading-relaxed">{question.question}</p>
                        {studyQ?.verseRef && (
                            <Badge variant="secondary" className="font-mono text-[9px] whitespace-nowrap px-1.5 h-4">
                                {studyQ.verseRef}
                            </Badge>
                        )}
                    </div>

                    {/* Tags / Badges - Visible in header */}
                    {(studyQ?.difficulty || appQ?.domain || appQ?.audience) && <div className="flex flex-wrap items-center gap-2 mt-2">
                        {studyQ?.difficulty && (
                            <Badge variant="outline" className={`text-[9px] uppercase tracking-tighter px-1.5 h-4 ${getDifficultyColors(studyQ.difficulty, isDarkMode)}`}>
                                {studyQ.difficulty}
                            </Badge>
                        )}

                        {appQ?.domain && (
                            <Badge variant="outline" className="text-[9px] uppercase tracking-tighter px-1.5 h-4 flex items-center gap-1 border-primary/20 bg-primary/5 text-primary/70">
                                <Icon className="h-2.5 w-2.5" />
                                {appQ.domain}
                            </Badge>
                        )}

                        {appQ?.audience && (
                            <Badge variant="outline" className="text-[9px] uppercase tracking-tighter px-1.5 h-4 flex items-center gap-1 border-blue-500/20 text-blue-600 bg-blue-500/5">
                                <Users className="h-2.5 w-2.5" />
                                {appQ.audience}
                            </Badge>
                        )}

                        {/* Expansion Prompt - Only for study questions when not expanded */}
                        {!isApplication && !isExpanded && (
                            <div className={cn(
                                "flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.15em] transition-colors ml-auto",
                                isDarkMode ? "text-primary/40 group-hover/card:text-primary/70" : "text-primary/60 group-hover/card:text-primary/90"
                            )}>
                                <Sparkles className="h-2.5 w-2.5" />
                                <span>Reveal Hint</span>
                            </div>
                        )}
                    </div>}
                </div>
            }
        >
            {(studyQ?.hint || question.answer) && <div className="space-y-4 pt-4">
                {studyQ?.hint && (
                    <div className="rounded text-xs flex gap-3 text-muted-foreground/80 italic font-light leading-relaxed border-l-2 border-primary/10 pl-4 py-1">
                        <Lightbulb className="h-4 w-4 text-primary/40 shrink-0" />
                        <p><span className={cn("font-bold not-italic uppercase tracking-widest mr-1", isDarkMode ? "text-primary/60" : "text-primary/80")}>Hint:</span> {studyQ.hint}</p>
                    </div>
                )}

                {question.answer && (
                    <div className="pt-2">
                        {!isAnswerRevealed ? (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setIsAnswerRevealed(true)}
                                className="w-full text-[10px] font-bold uppercase tracking-widest gap-2 bg-green-500/5 border-green-500/10 hover:bg-green-500/10 hover:border-green-500/20 text-green-700/70"
                            >
                                <CheckCircle2 className="h-3 w-3" />
                                Reveal a Suggested Answer
                            </Button>
                        ) : (
                            <div className={cn(
                                "p-4 rounded-xl border animate-in fade-in slide-in-from-top-1 duration-200",
                                isDarkMode ? "bg-green-500/5 border-green-500/10" : "bg-green-50 border-green-100"
                            )}>
                                <div className="flex gap-3 items-start">
                                    <div className={cn(
                                        "p-1.5 rounded-lg shrink-0",
                                        isDarkMode ? "bg-green-500/10" : "bg-green-500/10"
                                    )}>
                                        <CheckCircle2 className={cn("h-3.5 w-3.5", isDarkMode ? "text-green-400" : "text-green-600")} />
                                    </div>
                                    <div className="space-y-1">
                                        <div className="flex items-center justify-between">
                                            <span className={cn("font-bold text-[10px] uppercase tracking-widest block", isDarkMode ? "text-green-700/60" : "text-green-800/80")}>
                                                Suggested Answer
                                            </span>
                                            {!isApplication && (
                                                <button
                                                    onClick={() => setIsAnswerRevealed(false)}
                                                    className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground/40 hover:text-muted-foreground/70 transition-colors"
                                                >
                                                    Hide
                                                </button>
                                            )}
                                        </div>
                                        <p className="text-sm text-foreground/80 leading-relaxed italic font-serif text-pretty">
                                            {question.answer}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>}
        </DiscoveryCard>
    );
});

function LoadingSkeleton() {
    return (
        <div className="space-y-4 p-4">
            <Card>
                <CardHeader>
                    <Skeleton className="h-6 w-64" />
                </CardHeader>
                <CardContent className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                </CardContent>
            </Card>
            <Card>
                <CardHeader>
                    <Skeleton className="h-6 w-48" />
                </CardHeader>
                <CardContent>
                    <Skeleton className="h-4 w-full" />
                </CardContent>
            </Card>
        </div>
    );
}
